import classnames from 'classnames';
import React from 'react';

import Modal from 'app/components/Modal/Modal';
import {ApiAccessSubscription} from 'app/modules/Remote/Organization';
import {useUpcomingInvoices} from 'app/providers/ImageryContractProvider';
import * as costUtils from 'app/utils/costUtils';
import {StatusMaybe} from 'app/utils/hookUtils';

import cs from './ContractHistory.module.scss';
import {formatInvoiceDueDate, formatLineItem} from './utils';

export const SubscriptionPaymentSection: React.FunctionComponent<{
  organizationId: string;
  accessSubscriptionMaybe: StatusMaybe<ApiAccessSubscription>;
}> = ({organizationId, accessSubscriptionMaybe}) => {
  const accessSubscriptionIds = React.useMemo(() => {
    const sub = accessSubscriptionMaybe.status === 'some' && accessSubscriptionMaybe.value;
    if (sub && !sub.endDate && sub.currentPeriodEnd) {
      return [sub.id];
    }
    return [];
  }, [accessSubscriptionMaybe.status, accessSubscriptionMaybe.value]);

  const [accessSubscriptionUpcomingInvoiceMaybe] = useUpcomingInvoices(
    organizationId,
    accessSubscriptionIds
  );

  const accessSubscriptionUpcomingInvoice = React.useMemo(() => {
    if (
      accessSubscriptionUpcomingInvoiceMaybe.status === 'some' &&
      accessSubscriptionUpcomingInvoiceMaybe.value?.length
    ) {
      return accessSubscriptionUpcomingInvoiceMaybe.value[0];
    }
    return null;
  }, [accessSubscriptionUpcomingInvoiceMaybe.status, accessSubscriptionUpcomingInvoiceMaybe.value]);

  const subscriptionPaymentDueDate = React.useMemo(() => {
    if (!accessSubscriptionUpcomingInvoice) return '';
    return `Due ${formatInvoiceDueDate(accessSubscriptionUpcomingInvoice)}`;
  }, [accessSubscriptionUpcomingInvoice]);

  const isLoading =
    accessSubscriptionMaybe.status === 'unknown' ||
    accessSubscriptionUpcomingInvoiceMaybe.status === 'unknown';

  const [isRenewalDetailsModalOpen, setIsRenewalDetailsModalOpen] = React.useState(false);

  return (
    <div className={cs.planBillingCardSection}>
      <div className={classnames(cs.sectionLabel, isLoading ? 'bp5-skeleton' : '')}>
        Subscription payment
      </div>
      {accessSubscriptionUpcomingInvoice ? (
        <>
          {/*
              Uses 'total' rather than 'amount_due', unlike ImageryPaymentSection.
              We want to show access subscription invoices without account credit applied.
          */}
          <div className={cs.sectionValue}>
            {costUtils.formatCost(accessSubscriptionUpcomingInvoice['total'])}
          </div>
          <div className={cs.sectionSub}>{subscriptionPaymentDueDate}</div>
          <a onClick={() => setIsRenewalDetailsModalOpen(true)}>View details</a>
          <Modal
            isOpen={isRenewalDetailsModalOpen}
            className={cs.invoicesModalContent}
            onClose={() => setIsRenewalDetailsModalOpen(false)}
            headerText="Next subscription payment"
            hideActions
          >
            <ul>
              {accessSubscriptionUpcomingInvoice['lines']['data'].map((d) => formatLineItem(d))}
              <li>
                <div>Total due</div>
                <div>{costUtils.formatCost(accessSubscriptionUpcomingInvoice['total'])}</div>
              </li>
            </ul>
          </Modal>
        </>
      ) : isLoading ? (
        <>
          <div className={classnames(cs.sectionValue, 'bp5-skeleton')}>$0.00</div>
          <div className={classnames(cs.sectionSub, 'bp5-skeleton')}>Due MMM D</div>
          <a className="bp5-skeleton">View details</a>
        </>
      ) : (
        <div className={cs.sectionSub}>No upcoming invoice</div>
      )}
    </div>
  );
};
